import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, Eye, EyeOff, XCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [token, setToken] = useState('');
  const [isValidating, setIsValidating] = useState(true);
  const [isValidToken, setIsValidToken] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Hide Emergent badge
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.style.display = 'none';
    return () => { if (badge) badge.style.display = 'flex'; };
  }, []);

  // Validate token on mount
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (!tokenFromUrl) {
      setIsValidating(false);
      return;
    }

    setToken(tokenFromUrl);

    const validateToken = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/verify-reset-token/${tokenFromUrl}`);
        const data = await response.json();

        if (response.ok && data.valid) {
          setIsValidToken(true);
          setUserEmail(data.email);
        } else {
          setIsValidToken(false);
        }
      } catch (error) {
        setIsValidToken(false);
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (password.length < 6) {
      toast({
        title: 'Weak Password',
        description: 'Password must be at least 6 characters',
        variant: 'destructive'
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Password Mismatch',
        description: 'Passwords do not match',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: token,
          new_password: password
        })
      });

      const data = await response.json();

      if (response.ok) {
        setResetSuccess(true);
      } else {
        toast({
          title: 'Error',
          description: data.detail || 'Failed to reset password',
          variant: 'destructive'
        });
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Something went wrong. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Loading state
  if (isValidating) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating reset link...</p>
        </div>
      </div>
    );
  }

  // Invalid token state
  if (!isValidToken) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="pt-[env(safe-area-inset-top,12px)]" />
        
        <header className="px-4 py-4 flex items-center gap-3 border-b border-gray-200">
          <button onClick={() => navigate('/login')} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-xl font-bold text-gray-900">Reset Password</h1>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Invalid or Expired Link</h2>
          <p className="text-gray-600 text-center mb-8 max-w-sm">
            This password reset link is invalid or has expired. Please request a new one.
          </p>

          <div className="space-y-3 w-full max-w-sm">
            <Button
              onClick={() => navigate('/forgot-password')}
              className="w-full bg-blue-700 hover:bg-blue-800 py-6 rounded-full"
            >
              Request New Link
            </Button>
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              className="w-full py-6 rounded-full"
            >
              Back to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <div className="pt-[env(safe-area-inset-top,12px)]" />
        
        <header className="px-4 py-4 flex items-center gap-3 border-b border-gray-200">
          <h1 className="text-xl font-bold text-gray-900 ml-2">Password Reset</h1>
        </header>

        <div className="flex-1 flex flex-col items-center justify-center px-6 py-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-gray-900 mb-3 text-center">Password Reset!</h2>
          <p className="text-gray-600 text-center mb-8 max-w-sm">
            Your password has been successfully reset. You can now login with your new password.
          </p>

          <Button
            onClick={() => navigate('/login')}
            className="w-full max-w-sm bg-blue-700 hover:bg-blue-800 py-6 rounded-full text-lg font-semibold"
          >
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  // Reset password form
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <div className="pt-[env(safe-area-inset-top,12px)]" />
      
      <header className="px-4 py-4 flex items-center gap-3 border-b border-gray-200">
        <button onClick={() => navigate('/login')} className="p-2 hover:bg-gray-100 rounded-full">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Reset Password</h1>
          <p className="text-sm text-gray-600">Create a new password</p>
        </div>
      </header>

      <div className="flex-1 px-6 py-8">
        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6 mx-auto">
          <Lock className="w-8 h-8 text-blue-700" />
        </div>

        <p className="text-gray-600 text-center mb-2">
          Resetting password for:
        </p>
        <p className="text-gray-900 font-semibold text-center mb-8">
          {userEmail}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5 max-w-sm mx-auto">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-900 font-medium">New Password</Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="text-base py-6 pr-12 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" className="text-gray-900 font-medium">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="text-base py-6 text-gray-900 placeholder:text-gray-400 border-gray-300 bg-white"
              required
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white py-6 text-lg font-semibold rounded-full"
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
